import type { PromptConfig } from '@/types/edit/PromptConfig.ts';
import { logger } from '@/lib/logger.ts';
import type { SessionId } from '@/types/Session.ts';
import { apiService } from '@/services/ApiService.ts';
import { PromptService } from '@/services/PromptService.ts';
import { useClientStore } from '@/stores/ClientStore.ts';
import { useSessionStore } from '@/stores/SessionStore.ts';
import { PromptConfigService } from '@/services/config/PromptConfigService.ts';
import { VariableService } from '@/services/VariableService.ts';
import { createTextToast } from '@/utils/ToastUtils.ts';
import { ResponseError } from '../../../@generated/api-client';
import { StatusCodes } from 'http-status-codes';

export const runPromptAction = async (
    promptConfigId: PromptConfig['id'],
    sessionId: SessionId,
): Promise<void> => {
    const sessionStore = useSessionStore();
    if (sessionStore.isInsideWebView) {
        logger.debug(`runPromptAction called from inside web view, ignoring...`);

        return;
    }

    const promptConfig = PromptConfigService.getPromptConfig(promptConfigId);
    if (!promptConfig) {
        throw new Error(`Prompt config with id "${promptConfigId}" not found.`);
    }

    const variableChainHash = VariableService.calculateVariableChainHash(
        sessionId,
        promptConfig.stepId,
    );

    const sessionPromptId = PromptService.getSessionPromptId(
        sessionId,
        promptConfigId,
        variableChainHash,
    );

    if (PromptService.isPromptStarted(sessionPromptId)) {
        logger.warn(`Prompt config with name "${promptConfig.name}" has already been started.`);

        return;
    }

    const fullRawPrompt = `${promptConfig.rawText} ${promptConfig.outputFormatInstruction}`;
    const extractedVariableKeys = VariableService.extractVariableKeys(fullRawPrompt);

    const hydratedVariables = VariableService.hydrateVariables(
        extractedVariableKeys,
        sessionId,
        promptConfig.profileId,
    );

    const isPromptReadyToRun = VariableService.isPromptReadyToRun(hydratedVariables);

    if (!isPromptReadyToRun) {
        logger.warn(
            `Prompt with name "${promptConfig.name}" is not ready to run. Waiting for variables to be set.`,
            hydratedVariables,
        );

        return;
    }

    logger.debug(`Running "${promptConfig.name}" prompt with session id "${sessionId}."`);

    // @TODO: Move
    let responseFormat;
    if (promptConfig.formatAs === 'choice') {
        responseFormat = {
            type: 'array',
            items: {
                type: 'object',
                properties: {
                    title: {
                        type: 'string',
                        description: 'The title of the item.',
                    },
                    description: {
                        type: 'string',
                        description: 'The description of the item.',
                    },
                    smallTitle: {
                        type: 'string',
                        description: 'The optional small title of the item.',
                    },
                },
                required: ['title', 'description'],
                additionalProperties: false,
            },
        };
    }

    const clientStore = useClientStore();

    clientStore.streamsWaiting = [...clientStore.streamsWaiting, sessionPromptId];
    clientStore.streamStartDates[sessionPromptId] = Date.now();

    sessionStore.streamSessionIdMap[sessionPromptId] = sessionId;
    sessionStore.streamPromptConfigIdMap[sessionPromptId] = promptConfigId;
    sessionStore.streamVariableChainHashMap[sessionPromptId] = variableChainHash;

    setTimeout(() => {
        const promptStatus = PromptService.getPromptStatus(
            promptConfigId,
            sessionId,
            variableChainHash,
        );

        if (promptStatus.status !== 'finished' && promptStatus.status !== 'streaming') {
            if (!clientStore.streamsErrored.includes(sessionPromptId)) {
                clientStore.streamsErrored = [...clientStore.streamsErrored, sessionPromptId];
            }
        }
    }, 90_000); // 90 seconds

    try {
        await apiService.getApi().openrouterPromptV1({
            promptRequestDto: {
                modelId: promptConfig.modelId,
                rawPrompt: fullRawPrompt,
                variables: hydratedVariables,
                screenCaptures: promptConfig.includeScreenCapture
                    ? (clientStore.screenCapturesBySession[sessionId] ?? [])
                    : undefined,
                responseFormat: responseFormat ? JSON.stringify(responseFormat) : undefined,
                reasoningEffort: promptConfig.reasoningEffort,
                promptId: sessionPromptId,
            },
        });
    } catch (error) {
        let errorMessage = 'Prompt failed. Please try again.';
        if (error instanceof ResponseError) {
            try {
                const responseData = await error.response.json();
                if (typeof responseData.message === 'string') {
                    errorMessage = responseData.message;
                }
            } catch {
                // Keep the safe fallback message when the response has no JSON body.
            }

            createTextToast(
                error.response.status === StatusCodes.TOO_MANY_REQUESTS
                    ? 'Rate limited'
                    : 'Prompt failed',
                errorMessage,
                'destructive',
                true,
            );
        }

        logger.error('Prompt failed to run:', error);
        clientStore.streamErrors[sessionPromptId] = errorMessage;
        if (!clientStore.streamsErrored.includes(sessionPromptId)) {
            clientStore.streamsErrored = [...clientStore.streamsErrored, sessionPromptId];
        }
    }
};
